/* eslint-disable import/no-duplicates */
import React, { useCallback } from 'react'
import {
	createContext, ReactNode, useEffect, useReducer, useState, useMemo,
} from 'react'

import 'firebase/app'
import 'firebase/auth'
import 'firebase/firestore'
import truncateEthAddress from 'truncate-eth-address'
import {
	collection,
	doc, DocumentData, getDoc, getFirestore, onSnapshot, query, setDoc, where,
} from 'firebase/firestore'
import {
	getAuth, signInWithCustomToken, signInWithEmailAndPassword, signOut, UserCredential, signInAnonymously,
} from 'firebase/auth'

// eslint-disable-next-line import/no-unresolved
import { FirebaseApp, initializeApp } from 'firebase/app'
import axios from 'axios'
import { isEqual, isNil } from 'lodash'
import { useWeb3React } from '@web3-react/core'
import { Web3Provider } from '@ethersproject/providers'
import {
	ActionMap, AuthState, AuthUser, FirebaseContextType, NonceResponse, TLoginResult, TWeb3Context, VerifyResponse,
} from '../@types/auth'
//

import useWeb3 from '../hooks/useWeb3'
import { getApiUrl } from '../utils/firebase'
import { INftLight, IUserNft } from '../types/tokens'
import activateFirebaseEmulator, { getFirebase } from '../services/firebase'
// ----------------------------------------------------------------------

const ADMIN_EMAILS = ['demo@minimals.cc']

const GET_NONCE_URL = getApiUrl('auth-api-getNonceToSign')
const VERIFY_URL = getApiUrl('auth-api-verifySignedMessage')

const initialState: AuthState = {
	isAuthenticated: false,
	isInitialized: false,
	user: null,
}

enum Types {
  Initial = 'INITIALISE'
}

type FirebaseAuthPayload = {
  [Types.Initial]: {
    isAuthenticated: boolean
    user: AuthUser
  }
};

type FirebaseActions = ActionMap<FirebaseAuthPayload>[keyof ActionMap<FirebaseAuthPayload>];

const reducer = (state: AuthState, action: FirebaseActions) => {
	if (action.type === 'INITIALISE') {
		const { isAuthenticated, user } = action.payload
		return {
			...state,
			isAuthenticated,
			isInitialized: true,
			user,
		}
	}

	return state
}

const AuthContext = createContext<FirebaseContextType | null>(null)
// const AuthContext = createContext<FirebaseContextType>({ ...initialState })

function AuthProvider({ children }: { children: ReactNode }) {
	const {
		signMessage, account, active, activate, deactivate, error,
	} = useWeb3()

	const [profile, setProfile] = useState<DocumentData | undefined>()
	const [tokens, setTokens] = useState <Array<IUserNft>>([])
	const [state, dispatch] = useReducer(reducer, initialState)

	getFirebase()
	activateFirebaseEmulator()
	/*
	const [isFirebaseInitialized, setIsFirebaseInitialized] = useState<boolean>(false)
	if (!isFirebaseInitialized) {
		initializeApp(firebaseConfig)
		setIsFirebaseInitialized(true)
	}
	*/

	// const auth = getAuth(app as FirebaseApp)
	// const db = getFirestore(app as FirebaseApp)
	const auth = getAuth()
	const db = getFirestore()

	useEffect(() => {
		const doLogout = async () => {
			const { ethereum } = window as any
			if (ethereum && ethereum.on && !active && !error) {
				/*
				const handleConnect = () => {
					console.log('Handling \'connect\' event')
				// activate(injected)
				} */
				/* 	const handleChainChanged = (chainId: string | number) => {
					console.log('Handling \'chainChanged\' event with payload', chainId)
				// activate(injected)
				} */

				const handleAccountsChanged = async (accounts: string[]) => {
					// console.log('Handling \'accountsChanged\' event with payload', accounts)
					// console.log(`auth.currentUser?.uid=${auth.currentUser?.uid.toLowerCase()}`)
					// console.log(`accounts?.[0]=${accounts?.[0].toLowerCase()}`)
					if (!isEqual(auth.currentUser?.uid.toLowerCase(), accounts?.[0].toLowerCase())) {
						console.error('There seems to be a Wallet issue so logging the user out of Main Authentication System as a precaution,')
						await signOut(auth)
					}
				}
				/*
				const handleNetworkChanged = (networkId: string | number) => {
					console.log('Handling \'networkChanged\' event with payload', networkId)
				// activate(injected)
				} */

				// ethereum.on('connect', handleConnect)
				// ethereum.on('chainChanged', handleChainChanged)
				ethereum.on('accountsChanged', handleAccountsChanged)
				// ethereum.on('networkChanged', handleNetworkChanged)

				return () => {
					if (ethereum.removeListener) {
						// ethereum.removeListener('connect', handleConnect)
						// ethereum.removeListener('chainChanged', handleChainChanged)
						ethereum.removeListener('accountsChanged', handleAccountsChanged)
						// ethereum.removeListener('networkChanged', handleNetworkChanged)
					}
				}
			}
			return () => {}
		}

		doLogout()
	}, [active, activate, error, auth])

	/*  useEffect(() => {
		const doLogout = async () => {
			console.log(`account=${account}`)
			console.log(`active=${active}`)
			await activate()
			if (isNil(account)) {
				console.error('There seems to be no Wallet connected so logging the user out of Main Authentication System as a precaution,')
				deactivate()
				await signOut(auth)
			}
		}
		doLogout()
	}, [account, active, auth, activate, deactivate]) */

	useEffect(
		() => auth.onAuthStateChanged((user) => {
			// if (user && account && active) {
			if (user) {
				const docRef = doc(db, 'users', user.uid)
				getDoc(docRef)
					.then((doc) => {
						if (doc.exists()) {
							setProfile(doc.data())
						}
					})
					.catch((error) => {
						console.error(error)
					})

				dispatch({
					type: Types.Initial,
					payload: { isAuthenticated: true, user },
				})
			} else {
				dispatch({
					type: Types.Initial,
					payload: { isAuthenticated: false, user: null },
				})
			}
		}),
		[account, active, auth, db, dispatch],
	)

	useEffect(() => {
		if (auth.currentUser) {
			const docRef = doc(db, 'users', auth.currentUser.uid)
			const unsubscribe = onSnapshot(docRef, (doc) => {
				// console.log('got new user info', JSON.stringify(doc.data()))
				if (doc.exists()) {
					setProfile(doc.data())
				}
			})

			return (() => {
				unsubscribe()
			})
		}
		return (() => {})
	}, [auth.currentUser, db])

	useEffect(() => {
		if (auth.currentUser) {
			// const collectionRef = collection(db, 'users', auth.currentUser.uid, 'customizedTokens')
			const collectionRef = collection(db, 'customizedTokens')
			const q = query(
				collectionRef,
				where('uid', '==', auth.currentUser.uid),
			)
			const unsubscribe = onSnapshot(q, (results) => {
				setTokens(results.docs.map((d) => ({
					...d.data() as INftLight,
					id: d.id,
				} as IUserNft)))
			})
			return (() => {
				unsubscribe()
			})
		}
		return (() => {})
	}, [auth.currentUser, db])

	// const login = useCallback(async (email: string, password: string):Promise<UserCredential> => signInWithEmailAndPassword(auth, email, password), [auth])
	const loginAnonymously = useCallback(async ():Promise<UserCredential> => signInAnonymously(auth), [auth])

	const loginWithWallet = useCallback(async (address: string):Promise<TLoginResult> => {
		try {
			// console.log('fn loginWithWallet')
			if (isNil(address)) {
				throw new Error('No Wallet Address Provided')
			}

			const { data: nonceResponse } = await axios.post<NonceResponse>(
				GET_NONCE_URL,
				{
					address,
				},
			)

			const isWhitelisted = nonceResponse?.status === 'WHITELISTED'

			if (!isWhitelisted) {
				return { userCredentials: undefined, isWhitelisted: false }
			}

			// const sig = await library?.provider.getSigner(address).signMessage(nonceResponse.nonce)
			const sig = await signMessage(address, nonceResponse.nonce)

			const { data: verifiedResponse } = await axios.post<VerifyResponse>(
				VERIFY_URL,
				{ address, signature: sig },
			)

			const userCredentials = await signInWithCustomToken(auth, verifiedResponse.token)
			return { userCredentials, isWhitelisted }
		} catch (err) {
			// console.log(`error in my belly = ${err}`)
			return { userCredentials: undefined, isWhitelisted: false }
		}
	}, [auth, signMessage])

	const logout = useCallback(async () => {
		await signOut(auth)
	}, [auth])

	/* useEffect(() => {
		console.log(`account=${account}`)
		console.log(`active=${active}`)
		if (isNil(account)) {
			console.error('There seems to be no Wallet connected so logging the user out of firebase as a precaution,')
			// loginWithWallet(account)
			logout()
		}
	}, [account, logout, active]) */

	/* const loginWithGoogle = () => {
		const provider = new GoogleAuthProvider()
		return signInWithPopup(auth, provider)
	}

	const loginWithFaceBook = () => {
		const provider = new FacebookAuthProvider()
		return signInWithPopup(auth, provider)
	}

	const loginWithTwitter = () => {
		const provider = new TwitterAuthProvider()
		return signInWithPopup(auth, provider)
	} */

	/* const register = useCallback((email: string, password: string, firstName: string, lastName: string) => createUserWithEmailAndPassword(auth, email, password)
		.then((res) => {
			// const db = getFirestore()
			const ref = doc(db, 'users', res.user?.uid)
			setDoc(ref, {
				uid: res.user?.uid,
				email,
				displayName: `${firstName} ${lastName}`,
			}, { merge: true })
		}), [auth, db])
*/

	/* const resetPassword = useCallback(async (email: string) => {
		await sendPasswordResetEmail(auth, email)
	}, [auth]) */

	// const auth = { ...state.user }

	const { currentUser } = auth
	const value = useMemo(() => {
		const {
			country, address, state: userLocationState, city, zipCode, about, isPublic,
		} = profile || {}
		const {
			uid, displayName,
		} = currentUser || {}

		const email = currentUser?.email || profile?.email
		const isDisplayNameSet = !!(displayName || profile?.displayName)
		return ({
			...state,
			method: 'firebase',
			user: {
				id: uid,
				email,
				photoURL: currentUser?.photoURL || profile?.photoURL,
				// displayName: currentUser?.displayName || profile?.displayName,
				isDisplayNameSet,
				displayName: displayName ?? profile?.displayName ?? ((uid) && truncateEthAddress(uid)) ?? '',
				role: ADMIN_EMAILS.includes(email ?? '') ? 'admin' : 'user',
				phoneNumber: currentUser?.phoneNumber || profile?.phoneNumber || '',
				country: country || '',
				address: address || '',
				state: userLocationState || '',
				city: city || '',
				zipCode: zipCode || '',
				about: about || '',
				isPublic: isPublic || false,
			},
			// login,
			loginWithWallet,
			// register,
			// loginWithGoogle,
			// loginWithFaceBook,
			// loginWithTwitter,
			logout,
			// resetPassword,
			updateProfile: () => {},
			tokens,
			walletAccount: account,
			loginAnonymously,
		} as FirebaseContextType)
	}, [currentUser, loginWithWallet, logout, profile, state, tokens, account, loginAnonymously])

	return (
		<AuthContext.Provider
			value={value}
		>
			{children}
		</AuthContext.Provider>
	)
}

export { AuthContext, AuthProvider }
